// Client data layer. The single place the apps talk to the backend (`/api/*`),
// which proxies Airtable. Replaces the old pattern of calling api.airtable.com
// directly with a bundled token. Every request carries the signed-in user's
// Firebase ID token; the server derives identity from it and scopes the data,
// so we no longer pass (or trust) a phone number / record id from the client.
//
// This is also the one chokepoint for response.ok handling — every call either
// returns parsed JSON or throws an ApiError, so callers never parse a failed
// response as if it succeeded.

import { auth } from '../firebase.js';

const API_BASE = '/api';

// Error carrying the HTTP status and the server's machine-readable code
// (e.g. 'INSUFFICIENT_VOUCHERS', 'NO_FAMILY') so callers can branch on it.
export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || code || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function authHeader() {
  const user = auth?.currentUser;
  if (!user) {
    // Not signed in — there's no token to send. Callers run this only on
    // authenticated paths, so this indicates a dropped session.
    throw new ApiError(401, 'NOT_SIGNED_IN', 'You are not signed in. Please log in again.');
  }
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function handleResponse(response) {
  if (!response.ok) {
    // Try to surface the server's structured error; fall back to status text.
    let code = `HTTP_${response.status}`;
    let message = response.statusText;
    try {
      const data = await response.json();
      code = data.error || code;
      message = data.message || message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, code, message);
  }

  if (response.status === 204) return null;
  return response.json();
}

async function request(path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(await authHeader()),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    // Offline / DNS / CORS — no HTTP status at all.
    throw new ApiError(0, 'NETWORK', `Network error: ${networkErr.message}`);
  }
  return handleResponse(response);
}

// Unauthenticated request — used only for the pre-login phone check, which by
// definition runs before the user has a Firebase token. Same error handling as
// request(), minus the auth header.
async function publicRequest(path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    throw new ApiError(0, 'NETWORK', `Network error: ${networkErr.message}`);
  }
  return handleResponse(response);
}

// Pre-login gate: is this phone allowed to sign in? `app` is 'family' or
// 'restaurant'. Returns { registered: boolean }. Callers fail OPEN (proceed to
// send the code) if this throws, so a check outage never locks anyone out.
export function checkPhone(phone, app) {
  return publicRequest('/auth/check-phone', { method: 'POST', body: { phone, app } });
}

// ───────────────────────── Family app ─────────────────────────

// The caller's own Families record: { id, fields }.
export function getFamily() {
  return request('/family/me');
}

// The caller's active orders: { records: [...] }. The client keeps doing the
// language-aware join against its loaded restaurant/menu data.
export function getOrders() {
  return request('/family/orders');
}

// Program data: { restaurants, hours, hours2 } (raw record arrays).
export function getRestaurants() {
  return request('/restaurants');
}

// Place an order. amounts is { 1..6 } meal counts. Server validates the voucher
// balance and returns { id, vouchersRemaining }.
export function placeOrder({ restaurantId, amounts }) {
  return request('/family/orders', { method: 'POST', body: { restaurantId, amounts } });
}

// ─────────────────────── Restaurant app ───────────────────────

// The caller's own Restaurants record: { id, fields }.
export function getRestaurantProfile() {
  return request('/restaurant/me');
}

// This restaurant's orders: { records, familyCodeById }. Staff orders are
// filtered and family codes resolved server-side; the client keeps the
// language-aware menu/date formatting.
export function getRestaurantOrders() {
  return request('/restaurant/orders');
}

// Walk-in step 1: look up a family by code. Returns minimal info (no phone
// numbers) or throws ApiError(404, 'NOT_FOUND').
export function verifyWalkInFamily(familyCode) {
  return request('/restaurant/walkin/verify-family', { method: 'POST', body: { familyCode } });
}

// Walk-in step 2: verify the last 4 digits of the family's phone server-side.
// Returns { match: boolean }.
export function verifyWalkInPhone(familyId, last4) {
  return request('/restaurant/walkin/verify-phone', { method: 'POST', body: { familyId, last4 } });
}

// Place a walk-in order. Server binds it to the authenticated restaurant and
// returns { id, vouchersRemaining }.
export function submitWalkInOrder({ familyId, amounts }) {
  return request('/restaurant/walkin/orders', { method: 'POST', body: { familyId, amounts } });
}

// Mark an order picked up or cancelled. status is { pickedUp: true } or
// { cancelled: true }. Server enforces that the order belongs to this restaurant.
export function setOrderStatus(orderId, status) {
  return request(`/restaurant/orders/${encodeURIComponent(orderId)}`, { method: 'PATCH', body: status });
}
